import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { Save, Trash2, ArrowLeft, Plus, ChevronRight, List, CreditCard, Search, FileText, 
  Percent, Tag, Paperclip, History, Zap, ChevronLeft, ChevronRight as ChevronRightIcon, 
  Workflow, Type, Minus, Printer, Info} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import TaxCategoryModal from './TaxCategoryModal';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import CreatePOModal from './CreatePOModal';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import SmartPrintButton from './PrintButton'; 
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const PRDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [fieldMaps, setFieldMaps] = useState({});
  const [activeTab, setActiveTab] = useState('PR');
  const [selectedLineIdx, setSelectedLineIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [selectedPRForPO, setSelectedPRForPO] = useState(null);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const { canCreate, canUpdate, canDelete } = usePermissions('pr');
  
  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [count, setCount] = useState(0); 
  const location = useLocation();
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';  

  const [data, setData] = useState({
    prnum: '', description: '', status: 'WAPPR', siteid: sessionSite, orgid: sessionOrg,
    requestedby: session.username || '', 
    totalcost: 0, tax_total: 0, grand_total: 0, 
    ref_mrnum: '', requireddate: '', vendor: '',
    lines: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  const isReadOnly = data.status === 'APPROVED' || data.status === 'APPR' || data.status === 'CLOSE' || data.status === 'CAN' || data.status === 'CLOSED';
  const isEditable = !isReadOnly && (isNew ? canCreate : canUpdate);

  const loadData = useCallback(async () => {
    if (!isNew) {
      try {
        const res = await axios.get(`/api/pr/${id}`);
        // حماية المصفوفات
        res.data.lines = Array.isArray(res.data.lines) ? res.data.lines : [];
        setData(res.data);
      } catch (err) { toast.error(t('common.load_fail')); }
    }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (isNew && data.orgid) {
        setData(prev => ({
          ...prev,
          description: '', status: 'WAPPR', siteid: session.site_id || '', orgid: session.org_id || '',
          requestedby: session.username || '', totalcost: 0, tax_total: 0, grand_total: 0,
          ref_mrnum: '', requireddate: '', vendor: '', lines: []
        }));         
        axios.get(`/api/pr/next-number/${data.orgid}`)
            .then(res => { setData(prev => ({ ...prev, prnum: res.data.next_prnum })); });
    }
  }, [isNew, data.orgid, session.site_id, session.org_id, session.username]);

  const loadNavigation = useCallback(async () => {
      const searchParams = new URLSearchParams(location.search);
      if (isNew) return;

      try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`/api/pr/${id}/navigation`, {
              params: {
                  all_sites: searchParams.get('all_sites') || "false", 
                  siteid: sessionSite,
                  orgid: sessionOrg,
                  filter_site: searchParams.get('siteid') || "", 
                  prnum: searchParams.get('prnum') || "",
                  description: searchParams.get('description') || "",
                  status: searchParams.get('status') || "",
                  sortBy: searchParams.get('sortBy') || "prnum",
                  sortDir: searchParams.get('sortDir') || "desc"
              },
              headers: { 'Authorization': `Bearer ${token}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search, sessionSite, sessionOrg]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const fetchWFStatus = async () => {
    if (isNew) return;
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/PR/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setWfContext(res.data);
    } catch (err) { console.error("WF Check Failed", err); }
  };

  useEffect(() => { fetchWFStatus(); }, [id, data.status]);
  
  const fetchCount = useCallback(async () => {
    if (!data.prnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/PR/${data.prnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [data.prnum, isNew]);

  const recalculateTotals = (lines) => {
    let totalBase = 0;
    let totalTax = 0;

    const updatedLines = lines.map(l => {
      const qty = parseFloat(l.qty || 0);
      const unitcost = parseFloat(l.unitcost || 0);
      const taxRate = parseFloat(l.taxrate || 0);
      
      const lineBase = qty * unitcost;
      const lineTax = lineBase * (taxRate / 100);
      
      totalBase += lineBase;
      totalTax += lineTax;

      return { ...l, taxamount: lineTax, linecost: lineBase + lineTax };
    });

    setData(prev => ({
      ...prev, lines: updatedLines, totalcost: totalBase, tax_total: totalTax, grand_total: totalBase + totalTax
    }));
  };

  const fetchAndFillMR = async (mrnum) => {
    try {
      const loadId = toast.loading(t('pr.messages.fetchingMRLines', { mrnum }));
      const res = await axios.get(`/api/inventory/mr/lines/${mrnum}`);
      const mrLines = res.data;

      if (mrLines && mrLines.length > 0) {
        const newLines = mrLines.map(line => ({
          itemnum: line.itemnum, description: line.description, qty: line.qty_requested || 0,
          unitcost: line.unitcost || 0, tax_id: 'VAT15', taxrate: 15,
          taxamount: (line.qty_requested * line.unitcost) * 0.15,
          linecost: (line.qty_requested * line.unitcost) * 1.15,
          glaccount: line.glaccount || '', mrlineid: line.mrlineid 
        }));

        const combinedLines = [...(data.lines || []), ...newLines];
        recalculateTotals(combinedLines);
        setData(prev => ({ ...prev, ref_mrnum: mrnum }));
        toast.success(t('pr.messages.loadedMRLines', { count: mrLines.length }), { id: loadId });
      } else {
        toast.error(t('pr.messages.noMRLines'), { id: loadId });
      }
    } catch (err) { toast.error(t('pr.messages.failedMRLines')); }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token'); 
      if (!token) {
        toast.error(t('common.sessionExpired'));
        navigate('/login');
        return;
      }

      const res = await axios.post('/api/pr/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(t('common.save_success'));
      if (isNew) navigate(`/pr/${res.data.prnum}`);
    } catch (err) { 
      toast.error(t('common.save_fail') + ": " + (err.response?.data?.detail || "System Error"));
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
      if (data.status !== 'WAPPR') {
          return Swal.fire({
              title: t('pr.swal.lockedTitle'),
              text: t('pr.swal.cantDeleteStatus', { status: data.status }),
              icon: 'error',
              confirmButtonColor: '#1a3353'
          });
      }
  
      const result = await Swal.fire({
          title: t('common.are_you_sure'),
          text: t('pr.swal.deleteWarnText'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#1a3353',
          confirmButtonText: t('common.confirm')
      });
  
      if (result.isConfirmed) {
          try {
              const token = localStorage.getItem('token');
              await axios.delete(`/api/pr/${data.prnum}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              toast.success(t('common.delete_success'));
              navigate('/pr'); 
          } catch (err) {
              toast.error(err.response?.data?.detail || t('common.delete_fail'));
          }
      }
  };  

  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', {
          module: 'PR', ref_id: id
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('workflow.toasts.initiated'));
      loadData();
      fetchWFStatus();
    } catch (err) { toast.error(t('workflow.toasts.failed')); }
  };

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    let extraFilters = { site_id: data.siteid, org_id: data.orgid };
    if (fieldName === 'ref_mrnum') extraFilters.status = 'APPR'; 

    setLookup({ isOpen: true, title: title, type: defaultType, field: fieldName, filters: extraFilters, targetIdx: lineIdx });
  };

  const handleLookupSelect = (row) => {
    // شبكة اصطياد القيم
    const val = row.taxid || row.mrnum || row.value || row.id || row.itemnum || row.comp_num;
    
    if (lookup.field === 'ref_mrnum') {
      fetchAndFillMR(val);
    } else if (lookup.field === 'tax_id') {
      const newLines = [...(data.lines || [])];
      const idx = lookup.targetIdx;
      newLines[idx].tax_id = row.taxid;
      newLines[idx].taxrate = row.taxrate || 0;
      recalculateTotals(newLines);
    } else if (lookup.field === 'itemnum' && lookup.targetIdx !== null) {
      const newLines = [...(data.lines || [])];
      const idx = lookup.targetIdx;
      newLines[idx].itemnum = row.itemnum;
      newLines[idx].description = row.description;
      newLines[idx].itemtype = row.record_type;
      newLines[idx].unitcost = row.avgcost || 0;
      if (row.tax_id) {
          newLines[idx].tax_id = row.tax_id;
          newLines[idx].taxrate = row.taxrate || 0;
      }
      recalculateTotals(newLines);
    } else {
      setData(prev => ({ ...prev, [lookup.field]: val }));
    }
    setLookup(p => ({ ...p, isOpen: false }));
  };

  const goTo = (target) => target && navigate(`/pr/${target}${location.search}`);

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);
  
  useEffect(() => {
    const openStatusModal = () => {
        if (isReadOnly && data.status === 'CAN') return Swal.fire({title: t('pr.swal.lockedTitle'), text: t('pr.swal.cantChangeCancel'), icon: 'lock'});
        setShowStatusModal(true);
    };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [isReadOnly, data.status, t]);

  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);
  
  useEffect(() => {
    const handleOpenTax = () => { setIsTaxModalOpen(true); };
    window.addEventListener('OPEN_TAX_CATEGORY', handleOpenTax);
    return () => { window.removeEventListener('OPEN_TAX_CATEGORY', handleOpenTax); };
  }, []);

  useEffect(() => {
    const handleOpenPO = () => { setSelectedPRForPO(true); };
    window.addEventListener('OPEN_CREATE_PO', handleOpenPO);
    return () => { window.removeEventListener('OPEN_CREATE_PO', handleOpenPO); };
  }, []);
  
  useEffect(() => { fetchCount(); }, [data.prnum, fetchCount]);
  
  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[1.0em] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup {...lookup} onClose={() => setLookup(p => ({ ...p, isOpen: false }))} onSelect={handleLookupSelect} />
      
      {isWFHistoryOpen && <WorkflowHistoryModal module="PR" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
      
      {isTaxModalOpen && (
        <TaxCategoryModal  
          isOpen={isTaxModalOpen}
          onClose={() => setIsTaxModalOpen(false)} 
          orgId={data.orgid}
        />
      )}
      
      {selectedPRForPO && (
        <CreatePOModal 
          isOpen={!!selectedPRForPO}
          prData={data}
          onClose={() => setSelectedPRForPO(null)}
          onComplete={(newPoNum) => {
            setSelectedPRForPO(null);
            navigate(`/po/${newPoNum}`); 
          }}
        />
      )}      
      
      {showStatusModal && (
        <StatusModal 
          currentStatus={data.status}
          recordId={data.prnum}
          appName="PR"
          domainId={data.status === 'APPR' ? 'CANCEL_ONLY' : fieldMaps['status']}
          onClose={() => setShowStatusModal(false)}
          onComplete={async () => {
                await loadData(); 
                const event = new CustomEvent('SHOW_CREATE_PO_MENU', { 
                    detail: { prnum: data.prnum, status: 'APPR' } 
                });
                window.dispatchEvent(event);
            }}          
        />)}
        
      {showHistoryModal && (
        <StatusHistoryModal isOpen={showHistoryModal} appName="PR" recordId={data.prnum} onClose={() => setShowHistoryModal(false)} />
      )}
      
      {!isNew && (
        <AttachmentsModal 
            isOpen={isDocModalOpen} 
            onClose={() => {setIsDocModalOpen(false);fetchCount();}} 
            ownertable="PR" 
            ownerid={data.prnum} 
        />
      )}

      {/* --- TOP TOOLBAR --- */}
      <div className={`bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0`}>
        <div className={`flex items-center gap-3`}>
          <button onClick={() => navigate('/pr')} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200 transition-transform active:scale-90">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1`}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRightIcon size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRightIcon size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2`}>
          {isEditable && (
          <button onClick={handleSave} disabled={loading || isReadOnly} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all active:scale-95 disabled:opacity-50 uppercase">
            <Save size={14} /> {loading ? t('common.saving') : t('common.save')}
          </button>
          )}

          {wfContext.can_start && !isReadOnly && (
            <button onClick={handleRouteWorkflow} className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2 text-[11px] font-black h-8 shadow-md px-4 rounded-sm transition-all active:scale-95 uppercase">
              <Zap size={14} fill="currentColor" /> {t('pr.actions.routeWf')}
            </button>
          )}

          <WorkflowActionModal moduleName="PR" refId={id} currentStatus={data.status} onActionComplete={loadData} />
          <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('pr.actions.wfHistory')}><Workflow size={16} className="text-orange-400"/></button>
          
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          
          <SmartPrintButton moduleName="PR" refId={`${data.prnum}`} />
          <button onClick={() => setShowStatusModal(true)} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.change_status')}><Tag size={16}/></button>
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.history')}><History size={16}/></button>
          
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-500/20 transition-colors rounded-sm" title={t('common.delete')}>
              <Trash2 size={16}/>
            </button>
          )}
          
          {/* Font Controls */}
          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 shadow-inner ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- HEADER FIELDS --- */}
      <div className={`bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 z-20`}>
        <HeaderField label={t('pr.fields.prNum')} value={data.prnum} isBold width="w-32" disabled isRTL={isRTL}/>
        <HeaderField label={t('assets.headers.desc')} value={data.description} onChange={v => setData(prev => ({...prev, description: v}))} width="w-[400px]" disabled={isReadOnly} isRTL={isRTL} placeholder={t('pr.placeholders.desc')}/>
        <HeaderField label={t('assets.headers.status')} value={data.status} disabled width="w-24" isRTL={isRTL}/>
        <HeaderField label={t('assets.headers.site')} value={data.siteid} disabled width="w-24" isRTL={isRTL}/>
      </div>

      {/* --- TABS --- */}
      <div className={`flex bg-white border-b border-gray-100 px-4 shrink-0 font-black uppercase`}>
        {['PR', 'PR Lines'].map(tKey => (
          <button 
            key={tKey} 
            onClick={() => setActiveTab(tKey)} 
            className={`px-8 py-3 flex items-center gap-2 transition-all tracking-tighter ${activeTab === tKey ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-gray-50' : 'text-gray-400 hover:text-gray-600'}`}
          >
            {tKey === 'PR' ? <FileText size={13}/> : <List size={13}/>} 
            {t(`pr.tabs.${tKey.replace(' ', '').toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        
        {/* TAB 1: PR INFO */}
        {activeTab === 'PR' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500 pb-20">
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('pr.sections.general')} isRTL={isRTL}>
                <div className={`grid grid-cols-2 gap-x-12 gap-y-4 ${isRTL ? 'dir-rtl' : ''}`}>
                  <EditableField label={t('pr.fields.requestedBy')} value={data.requestedby} disabled={isReadOnly} onChange={v => setData(prev => ({...prev, requestedby: v}))} isRTL={isRTL} />
                  <DateField label={t('pr.fields.reqDate')} value={data.requireddate} onChange={v => setData(prev => ({...prev, requireddate: v}))} isRTL={isRTL} />
                  <EditableField label={t('pr.fields.refMr')} value={data.ref_mrnum} onSearch={() => handleSearch('ref_mrnum', t('common.search'), 'materialrequest')} disabled={isReadOnly} isRTL={isRTL} />
                  <EditableField label={t('pr.fields.vendor')} value={data.vendor} disabled={isReadOnly} onChange={v => setData(prev => ({...prev, vendor: v}))} onSearch={() => handleSearch('vendor', t('common.search'), 'vendors')} isRTL={isRTL} />
                  <StaticField label={t('assets.headers.site')} value={data.siteid} isRTL={isRTL} />
                  <StaticField label={t('assets.list.col_org')} value={data.orgid} isRTL={isRTL} />
                </div>
              </SectionCard>
            </div>
            
            <div className="col-span-4 space-y-6">
              <SectionCard title={t('pr.sections.financial')} isRTL={isRTL}>
                <div className={`space-y-4 font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className={`flex justify-between border-b pb-1`}>
                    <span className="text-[9px] text-gray-400 font-black uppercase italic">{t('pr.financial.subtotal')}</span>
                    <span className="font-bold text-slate-700">{data.totalcost?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className={`flex justify-between border-b pb-1 text-red-500 font-black uppercase`}>
                    <span className="text-[9px]">{t('pr.financial.taxTotal')}</span>
                    <span>+{data.tax_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className={`flex justify-between bg-blue-50 p-3 rounded-md border border-blue-100 shadow-inner mt-4`}>
                    <span className={`font-black text-blue-900 uppercase text-[10px] flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CreditCard size={12}/> {t('pr.financial.grandTotal')}
                    </span>
                    <span className="text-xl font-black text-blue-900">{data.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {/* TAB 2: PR LINES */}
        {activeTab === 'PR Lines' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500 pb-20">
            <div className="bg-white border rounded shadow-xl overflow-hidden min-h-[400px]">
              <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-slate-50 border-b text-gray-500 font-black uppercase text-[0.8] tracking-widest">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('pr.tables.itemNum')}</th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc')}</th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('pr.tables.itemType')}</th>
                    <th className={`p-2 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('pr.tables.qty')}</th>
                    <th className={`p-2 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('pr.tables.unitCost')}</th>
                    <th className={`p-2 border-x text-center text-red-500 ${isRTL ? 'border-l' : 'border-r'}`}>{t('pr.tables.tax')} %</th>
                    <th className="p-2 text-center bg-blue-50/50">{t('pr.tables.lineTotal')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y font-bold text-[12px]">
                  {(Array.isArray(data.lines) ? data.lines : []).map((l, i) => (
                    <tr key={i} onClick={() => setSelectedLineIdx(i)} className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedLineIdx === i ? 'bg-[#e8f0fe]' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <td className="p-2 border-x text-center">{selectedLineIdx === i && <ChevronRight size={12} className={isRTL ? 'rotate-180 mx-auto' : 'mx-auto'}/>}</td>
                      <td className={`p-2 border-x font-black text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{l.itemnum || '---'}</td>
                      <td className={`p-2 border-x text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>{l.description}</td>
                      <td className={`p-2 border-x text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>{l.itemtype}</td>
                      <td className={`p-2 border-x text-center font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{l.qty}</td>
                      <td className={`p-2 border-x text-center font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{l.unitcost?.toLocaleString()}</td>
                      <td className={`p-2 border-x text-center font-black text-red-500 ${isRTL ? 'border-l' : 'border-r'}`}>{l.taxrate}%</td>
                      <td className="p-2 text-center font-black text-green-700 bg-green-50/10 font-mono tracking-tighter">
                        {parseFloat(l.linecost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                    </tr>
                  ))}
                  {(!data.lines || data.lines.length === 0) && (
                    <tr><td colSpan="8" className="p-16 text-center text-gray-300 italic uppercase font-black text-[0.9em] tracking-widest">{t('pr.messages.noLines')}</td></tr>
                  )}
                </tbody>
              </table>
              <button 
                onClick={() => {
                  setData(prev => {
                      const currentLines = Array.isArray(prev.lines) ? prev.lines : [];
                      const updated = [...currentLines, { itemnum: '', description: '', qty: 1, unitcost: 0, tax_id: '', taxrate: 0, taxamount: 0, linecost: 0, glaccount: '' }];
                      setSelectedLineIdx(updated.length - 1);
                      return {...prev, lines: updated};
                  });
                }} 
                disabled={isReadOnly} 
                className={`m-4 bg-[#3c70a4] text-white px-6 py-1.5 rounded-sm font-black text-[9px] uppercase hover:bg-black transition-all flex items-center gap-2 shadow-sm active:scale-95 ${isRTL ? 'float-right' : ''}`}
              >
                <Plus size={14}/> {t('common.new_row')}
              </button>
            </div>
            
            {/* Editor Box */}
            {selectedLineIdx !== null && data.lines && data.lines[selectedLineIdx] && (
              <div className={`bg-white border p-6 grid grid-cols-5 gap-6 shadow-2xl rounded-sm border-t-4 border-t-blue-800 animate-in zoom-in-95 ${isRTL ? 'dir-rtl' : ''}`}>
                <EditableField label={t('pr.tables.itemNum')} value={data.lines[selectedLineIdx].itemnum} onSearch={() => handleSearch('itemnum', t('common.search'), 'itemstools', selectedLineIdx)} disabled={isReadOnly} isRTL={isRTL} />
                <EditableField label={t('pr.tables.qty')} type="number" value={data.lines[selectedLineIdx].qty} disabled={isReadOnly} onChange={v => {
                    setData(prev => {
                        const newLines = Array.isArray(prev.lines) ? [...prev.lines] : [];
                        newLines[selectedLineIdx].qty = v;
                        recalculateTotals(newLines);
                        return { ...prev };
                    });
                }} isRTL={isRTL} />
                <EditableField label={t('pr.tables.unitCost')} type="number" value={data.lines[selectedLineIdx].unitcost} disabled={isReadOnly} onChange={v => {
                    setData(prev => {
                        const newLines = Array.isArray(prev.lines) ? [...prev.lines] : [];
                        newLines[selectedLineIdx].unitcost = v;
                        recalculateTotals(newLines);
                        return { ...prev };
                    });
                }} isRTL={isRTL} />
                <EditableField label={`${t('pr.tables.taxCat')} (${data.lines[selectedLineIdx].taxrate || 0}%)`} value={data.lines[selectedLineIdx].tax_id} onSearch={() => handleSearch('tax_id', t('common.search'), 'tax_category', selectedLineIdx)} disabled={isReadOnly} isRTL={isRTL} />
                <EditableField label={t('pr.fields.glAccount')} value={data.lines[selectedLineIdx].glaccount} disabled={isReadOnly} onChange={v => {
                    setData(prev => {
                        const newLines = Array.isArray(prev.lines) ? [...prev.lines] : [];
                        newLines[selectedLineIdx].glaccount = v;
                        return {...prev, lines: newLines};
                    });
                }} isRTL={isRTL} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- المكونات المساعدة ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, placeholder, isRTL }) => (
  <div className={`flex flex-col px-5 border-gray-100 ${isRTL ? 'border-l last:border-0 text-right' : 'border-r last:border-0 text-left'}`}>
    <label className="text-[8px] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input 
        className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 placeholder:font-normal placeholder:lowercase placeholder:italic ${isRTL ? 'text-right' : 'text-left'}`} 
        placeholder={placeholder}
        value={value || ''} 
        disabled={disabled} 
        onChange={e => onChange && onChange(e.target.value)} 
    />
  </div>
);

const SectionCard = ({ title, children, isRTL }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md border-gray-200">
    <div className={`p-2.5 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center gap-2 italic ${isRTL ? 'flex-row-reverse' : ''}`}>
      <div className="w-1.5 h-4 bg-orange-600 rounded-full shadow-sm"></div> {title}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all py-1.5 px-1 bg-transparent ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-gray-400 font-black text-[0.8em] uppercase tracking-widest italic leading-none">{label}</label>
    <div className={`flex items-center justify-between pb-0.5 h-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
      <input 
        type={type} 
        className={`outline-none font-black text-blue-900 bg-transparent w-full text-[1.0em] tracking-tighter placeholder:font-normal placeholder:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`} 
        value={value || ''} 
        disabled={disabled} 
        onChange={e => onChange && onChange(e.target.value)} 
      />
      {onSearch && !disabled && (
        <Search 
            size={14} 
            className="text-blue-300 cursor-pointer hover:text-blue-800 transition-all hover:scale-110 active:scale-90" 
            onClick={onSearch} 
        />
      )}
    </div>
  </div>
);

const StaticField = ({ label, value, isRTL }) => (
  <div className={`flex flex-col border-b border-gray-100 py-1.5 px-1 bg-white/40 shadow-inner rounded-sm mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic">{label}</label>
    <div className="font-black uppercase tracking-tighter text-blue-900 text-[12px] mt-0.5 italic">{value || '---'}</div>
  </div>
);

const DateField = ({ label, value, onChange, isRTL }) => (
  <div className={`flex flex-col border-b border-gray-300 py-1.5 px-1 group focus-within:border-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-tighter italic">{label}</label>
    <input 
        type="date" 
        className={`outline-none font-black text-blue-900 bg-transparent py-0.5 text-[12px] ${isRTL ? 'text-right' : 'text-left'}`} 
        value={value ? value.split('T')[0] : ''} 
        onChange={e => onChange(e.target.value)} 
    />
  </div>
);

export default PRDetails;